import { onMount } from 'solid-js';
import { sportradarLangISO } from '@bz/i18n/src/langISO.config';

import { initGlobalSir } from '../../public/sdk/sir';
import { TEAM_INVERT_SPORTS } from '../constants/sports';
import { initSportradarDataInterceptor } from './useSportraderData';

type SIRGlobal = {
  (...args: any[]): void;
  q?: any[];
  l?: number;
  o?: any;
};

declare global {
  interface Window {
    SIR?: SIRGlobal;
  }
}

let usePlayerModulePromise: Promise<typeof import('./usePlayer')> | null = null;

const loadUsePlayerModule = async () => {
  if (import.meta.env.SSR) return null;
  usePlayerModulePromise ??= import('./usePlayer');
  return usePlayerModulePromise;
};

export function useSIR(loadImmediately = true) {
  const timeout = 3000;

  const init = () => {
    if (typeof window === 'undefined') return;
    if (window.SIR) return; // 已加载

    initSportradarDataInterceptor();

    const sportradarLang = sportradarLangISO[setting.lang as keyof typeof sportradarLangISO] ?? 'en';
    initGlobalSir({
      language: sportradarLang,
    });
    /**
     * 需要反转主客队显示位置的运动类目
     * SportIdMap.AMERICAN_FOOTBALL,
     * SportIdMap.BASEBALL,
     */
    const sid: Record<string, boolean> = {};
    TEAM_INVERT_SPORTS.forEach((sport) => {
      sid[sport] = true;
    });
    setupTeamInvert({ sid });
  };

  const scheduleIdle = () => {
    if (typeof window === 'undefined') return;
    const run = () => init();
    if (loadImmediately) {
      run();
    } else {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout });
      } else {
        setTimeout(run, timeout);
      }
    }
  };

  onMount(() => {
    if (import.meta.env.SSR) return;
    scheduleIdle();
  });

  return {
    /**
     * 调用 SIR SDK 的方法
     */
    emit: (...args: any[]) => {
      // 等待 SIR SDK 加载完成后再调用
      if (import.meta.env.SSR) return;
      const [eventName, selector, ...rest] = args;
      const isLiveStream = rest.some((item) => typeof item === 'object' && item.isLiveStream);
      const options = rest.find((item) => typeof item === 'object' && item.streamsList);
      const forceSwitchToTracker = rest.some((item) => typeof item === 'object' && item.forceSwitchToTracker);

      if (eventName === 'removeWidget') {
        void (async () => {
          const usePlayerModule = await loadUsePlayerModule();
          usePlayerModule?.usePlayer().cleanPlayer(selector);

          if (window.SIR) {
            window.SIR(...args);
          } else {
            console.warn('SIR SDK not loaded yet');
          }
        })();
        return;
      }

      if (isLiveStream || forceSwitchToTracker) {
        void (async () => {
          const usePlayerModule = await loadUsePlayerModule();
          if (!usePlayerModule) return;

          const { cleanPlayer, createPlayer } = usePlayerModule.usePlayer(options?.streamsList || []);

          if (isLiveStream) {
            window.SIR?.('removeWidget', selector);
            const videoDom = document.querySelector(`${selector} video`);
            if (videoDom) {
              console.log('🚀 ~ useSIR ~ options?.streamsList:', videoDom);
              return;
            }
            const src = options?.streamsList?.[0]?.streamUrl;
            if (src) {
              await createPlayer(selector, src);
            }
            return;
          }

          if (forceSwitchToTracker) {
            cleanPlayer(selector);
            window.SIR?.('removeWidget', selector);
          }

          if (window.SIR) {
            window.SIR(...args);
          } else {
            console.warn('SIR SDK not loaded yet');
          }
        })();
        return;
      }

      if (window.SIR) {
        // console.log('SIR SDK is ready, emitting event:', args);
        window.SIR(...args);
      } else {
        console.warn('SIR SDK not loaded yet');
      }
    },
  };
}

export function useSIRLang() {
  return {
    lang: setting.lang,
    setLang: (lang: string) => {
      const sportradarLang = sportradarLangISO[lang as keyof typeof sportradarLangISO] ?? 'en';
      window.SIR?.('changeLanguage', sportradarLang);
    },
  };
}

/**
 * 配置主客队显示位置
 * 使用 Sportradar 的 changeTeamInvert 方法来控制主客队的左右显示位置
 *
 * 注意：此方法会强制重新加载所有 widgets，内部 widget 状态会丢失
 *
 * @param options 反转配置选项
 * - all: boolean - 反转所有比赛（默认 home 在左，away 在右，NFL 和棒球除外）
 * - sid: Record<string, boolean> - 按运动 ID 反转，例如 { sid: { 3: true }} 反转所有棒球比赛
 * - rcid: Record<string, boolean> - 按真实分类 ID 反转
 * - utid: Record<string, boolean> - 按唯一锦标赛 ID 反转
 *
 * @example
 * // 反转所有比赛的主客队位置
 * setupTeamInvert({ all: true });
 *
 * // 只反转棒球比赛（运动 ID 3）
 * setupTeamInvert({ sid: { 3: true } });
 */
export function setupTeamInvert(options: {
  all?: boolean;
  sid?: Record<string, boolean>;
  rcid?: Record<string, boolean>;
  utid?: Record<string, boolean>;
}) {
  if (import.meta.env.SSR || typeof window === 'undefined') return;
  if (window.SIR) {
    window.SIR('changeTeamInvert', options);
  } else {
    console.warn('SIR SDK not loaded yet, cannot change team invert');
  }
}
